<script setup>
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import MagnifyingGlassMinusIcon from '@iconify-vue/ph/magnifying-glass-minus';
import MagnifyingGlassPlusIcon from '@iconify-vue/ph/magnifying-glass-plus';
import XIcon from '@iconify-vue/ph/x';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import { useRoute } from '@js/utils/route';
import { AnimatePresence, Motion } from 'motion-v';
import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    VisuallyHidden,
} from 'reka-ui';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const {
    images,
    align = 'left',
    size = 'medium',
} = defineProps({
    images: {
        type: Array,
        required: true,
    },
    align: {
        type: String,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
});

const selectedIndex = ref(null);

const selectedImage = computed(() =>
    selectedIndex.value !== null ? images[selectedIndex.value] : null,
);

const scale = ref(1);

const translateX = ref(0);

const translateY = ref(0);

const isDragging = ref(false);

const dragStart = ref({ x: 0, y: 0 });

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5;

const hasPrev = computed(
    () => selectedIndex.value !== null && selectedIndex.value > 0,
);
const hasNext = computed(
    () =>
        selectedIndex.value !== null && selectedIndex.value < images.length - 1,
);

function resetZoom() {
    scale.value = 1;
    translateX.value = 0;
    translateY.value = 0;
}

function goToPrev() {
    if (!hasPrev.value) return;
    resetZoom();
    selectedIndex.value--;
}

function goToNext() {
    if (!hasNext.value) return;
    resetZoom();
    selectedIndex.value++;
}

function onKeydown(e) {
    if (selectedIndex.value === null) return;
    if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
    } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
    }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

function zoomIn() {
    scale.value = Math.min(MAX_SCALE, scale.value + SCALE_STEP);
    if (scale.value === MIN_SCALE) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function zoomOut() {
    scale.value = Math.max(MIN_SCALE, scale.value - SCALE_STEP);
    if (scale.value === MIN_SCALE) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function onWheel(e) {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
}

function onPointerDown(e) {
    if (scale.value <= MIN_SCALE) return;
    isDragging.value = true;
    dragStart.value = {
        x: e.clientX - translateX.value,
        y: e.clientY - translateY.value,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
}

function onPointerMove(e) {
    if (!isDragging.value) return;
    translateX.value = e.clientX - dragStart.value.x;
    translateY.value = e.clientY - dragStart.value.y;
}

function onPointerUp() {
    isDragging.value = false;
}

function openLightbox(index) {
    selectedIndex.value = index;
}

const isLightboxOpen = computed({
    get: () => selectedIndex.value !== null,
    set: (value) => {
        if (!value) {
            selectedIndex.value = null;
        }
    },
});

watch(isLightboxOpen, (open) => {
    if (!open) resetZoom();
});

const { route } = useRoute();

const visibleStackImages = computed(() => images.slice(0, 3));

function getFileName(url) {
    return url.split('/').pop().split('?')[0];
}

function download(url) {
    const link = document.createElement('a');
    link.href = route('download-proxy', { url });
    link.download = getFileName(url);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
</script>

<template>
    <BaseCell :align="align">
        <div
            v-if="images.length > 0"
            class="relative cursor-zoom-in"
            :class="{
                'size-15': size === 'small' && images.length === 1,
                'size-30': size === 'medium' && images.length === 1,
                'size-45': size === 'large' && images.length === 1,
                'h-17 w-21': size === 'small' && images.length === 2,
                'h-32 w-36': size === 'medium' && images.length === 2,
                'h-47 w-51': size === 'large' && images.length === 2,
                'h-19 w-28': size === 'small' && images.length >= 3,
                'h-34 w-42': size === 'medium' && images.length >= 3,
                'h-49 w-57': size === 'large' && images.length >= 3,
            }"
            @click="openLightbox(0)"
        >
            <img
                v-for="(src, index) in visibleStackImages"
                :key="index"
                :src="src"
                class="absolute rounded border border-gray-300 bg-white object-contain"
                :class="{
                    'size-15 max-w-15': size === 'small',
                    'size-30 max-w-30': size === 'medium',
                    'size-45 max-w-45': size === 'large',
                }"
                :style="{
                    top: `calc(var(--spacing) * ${index * 2})`,
                    left: `calc(var(--spacing) * ${index * 6})`,
                    zIndex: visibleStackImages.length - index,
                }"
                alt="Cellule d'images"
            />

            <span
                v-if="images.length > 3"
                class="absolute -right-2 -bottom-2 z-10 rounded-full bg-indigo-600 px-1.5 py-0.5 text-xs leading-none text-white shadow"
            >
                +{{ images.length - 3 }}
            </span>
        </div>

        <DialogRoot v-model:open="isLightboxOpen">
            <DialogPortal forceMount>
                <AnimatePresence>
                    <Motion
                        v-if="isLightboxOpen"
                        key="overlay"
                        :initial="{ opacity: 0 }"
                        :animate="{ opacity: 1 }"
                        :exit="{ opacity: 0 }"
                        :transition="{ duration: 0.2 }"
                        class="fixed inset-0 z-50"
                    >
                        <DialogOverlay
                            forceMount
                            class="size-full bg-black/80"
                        />
                    </Motion>

                    <Motion
                        v-if="isLightboxOpen"
                        key="content"
                        :initial="{ opacity: 0, scale: 0.96 }"
                        :animate="{ opacity: 1, scale: 1 }"
                        :exit="{ opacity: 0, scale: 0.96 }"
                        :transition="{
                            type: 'spring',
                            duration: 0.3,
                            bounce: 0.1,
                        }"
                        class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
                    >
                        <DialogContent
                            forceMount
                            class="pointer-events-auto relative max-h-[90vh] max-w-[90vw] focus:outline-none"
                            :aria-describedby="undefined"
                        >
                            <VisuallyHidden>
                                <DialogTitle />
                            </VisuallyHidden>

                            <div
                                class="absolute -top-12 right-0 left-0 flex items-center justify-between"
                            >
                                <div class="flex gap-2">
                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 hover:bg-indigo-700"
                                        title="Télécharger"
                                        @click="download(selectedImage)"
                                    >
                                        <DownloadSimpleIcon class="size-5" />
                                    </button>

                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale <= 1"
                                        title="Dézoomer"
                                        @click="zoomOut"
                                    >
                                        <MagnifyingGlassMinusIcon
                                            class="size-5"
                                        />
                                    </button>

                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale >= 5"
                                        title="Zoomer"
                                        @click="zoomIn"
                                    >
                                        <MagnifyingGlassPlusIcon
                                            class="size-5"
                                        />
                                    </button>
                                </div>

                                <DialogClose
                                    class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 hover:bg-indigo-700"
                                >
                                    <XIcon class="size-5" />
                                </DialogClose>
                            </div>

                            <button
                                title="Précédent"
                                @click="goToPrev"
                                :disabled="!hasPrev"
                                class="absolute top-1/2 -left-12 z-50 aspect-square -translate-y-1/2 rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                            >
                                <CaretLeftIcon class="size-5" />
                            </button>

                            <button
                                title="Suivant"
                                @click="goToNext"
                                :disabled="!hasNext"
                                class="absolute top-1/2 -right-12 z-50 aspect-square -translate-y-1/2 rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                            >
                                <CaretRightIcon class="size-5" />
                            </button>

                            <div
                                v-if="selectedImage"
                                class="overflow-hidden rounded-lg bg-white shadow-lg"
                                @wheel="onWheel"
                                @dblclick="resetZoom"
                            >
                                <img
                                    :src="selectedImage"
                                    :style="{
                                        transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
                                    }"
                                    class="max-h-[85vh] max-w-[85vw] object-contain transition-transform duration-150"
                                    :class="{
                                        'cursor-grab': scale > 1 && !isDragging,
                                        'cursor-grabbing': isDragging,
                                        '!duration-0': isDragging,
                                    }"
                                    draggable="false"
                                    @pointerdown="onPointerDown"
                                    @pointermove="onPointerMove"
                                    @pointerup="onPointerUp"
                                    alt="Image sélectionnée dans la lightbox"
                                />
                            </div>
                        </DialogContent>
                    </Motion>
                </AnimatePresence>
            </DialogPortal>
        </DialogRoot>
    </BaseCell>
</template>
